/**
 * Icon Mapper Utility
 * 
 * Maps EspoCRM entity names and icon classes to Material-UI icon components.
 * This utility provides a generic way to map entity names to appropriate icons.
 */

import { ComponentType } from 'react';
import TableChartIcon from '@mui/icons-material/TableChart';

// Import common Material-UI icons
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NoteIcon from '@mui/icons-material/Note';
import TaskIcon from '@mui/icons-material/Task';
import EventIcon from '@mui/icons-material/Event';
import MeetingIcon from '@mui/icons-material/Groups';
import CallIcon from '@mui/icons-material/Call';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';

/**
 * Entity name to icon mapping
 * Maps common EspoCRM entity names to Material-UI icons
 */
const ENTITY_ICON_MAP: Record<string, ComponentType<any>> = {
  // Core entities
  Account: BusinessIcon,
  Contact: PersonIcon,
  Lead: PersonIcon,
  Opportunity: AttachMoneyIcon,
  Case: AssignmentIcon,
  Document: DescriptionIcon,
  Email: EmailIcon,
  Call: CallIcon,
  Meeting: MeetingIcon,
  Task: TaskIcon,
  Event: EventIcon,
  Note: NoteIcon,
  
  // Common custom entities
  User: PersonIcon,
  Team: PeopleIcon,
  Role: SettingsIcon,
  
  // Product/Inventory entities
  Product: InventoryIcon,
  CProduct: InventoryIcon,
  ProductCategory: CategoryIcon,
  Inventory: Inventory2Icon,
  CInventario: Inventory2Icon,
  
  // Order/Delivery entities
  Order: ShoppingCartIcon,
  COrder: ShoppingCartIcon,
  Delivery: LocalShippingIcon,
  CDelivery: LocalShippingIcon,
  Shipment: LocalShippingIcon,
  
  // Financial entities
  Invoice: ReceiptIcon,
  Payment: PaymentIcon,
  Quote: AttachMoneyIcon,
  Expense: AttachMoneyIcon,
  
  // Location entities
  Location: StoreIcon,
  Address: StoreIcon,
  
  // Dashboard/Analytics
  Dashboard: DashboardIcon,
  Report: AssessmentIcon,
  Analytics: AnalyticsIcon,
  
  // Other common entities
  Folder: FolderIcon,
  File: DescriptionIcon,
  Calendar: CalendarTodayIcon,
  Production: ProductionQuantityLimitsIcon,
};

/**
 * Icon class to icon mapping (for EspoCRM iconClass)
 * Maps common EspoCRM icon classes to Material-UI icons
 */
const ICON_CLASS_MAP: Record<string, ComponentType<any>> = {
  'fa fa-user': PersonIcon,
  'fa fa-users': PeopleIcon,
  'fa fa-building': BusinessIcon,
  'fa fa-envelope': EmailIcon,
  'fa fa-phone': PhoneIcon,
  'fa fa-calendar': CalendarTodayIcon,
  'fa fa-file': DescriptionIcon,
  'fa fa-folder': FolderIcon,
  'fa fa-shopping-cart': ShoppingCartIcon,
  'fa fa-truck': LocalShippingIcon,
  'fa fa-money': AttachMoneyIcon,
  'fa fa-credit-card': PaymentIcon,
  'fa fa-dashboard': DashboardIcon,
  'fa fa-bar-chart': AssessmentIcon,
  'fa fa-cog': SettingsIcon,
  'fa fa-tasks': TaskIcon,
  'fa fa-sticky-note': NoteIcon,
};

/**
 * Get icon component for an entity name
 * 
 * @param entityName - The entity name (e.g., "Account", "Contact")
 * @returns Material-UI icon component, or TableChartIcon as default
 */
export function getEntityIcon(entityName: string | undefined | null): ComponentType<any> {
  if (!entityName) {
    return TableChartIcon;
  }
  
  return ENTITY_ICON_MAP[entityName] || TableChartIcon;
}

/**
 * Get icon component for an icon class
 * 
 * @param iconClass - The icon class from EspoCRM (e.g., "fa fa-user")
 * @returns Material-UI icon component, or TableChartIcon as default
 */
export function getIconClassIcon(iconClass: string | undefined | null): ComponentType<any> {
  if (!iconClass) {
    return TableChartIcon;
  }
  
  return ICON_CLASS_MAP[iconClass] || TableChartIcon;
}

/**
 * Get icon component for a navigation item
 * Prioritizes entity name mapping, then icon class mapping
 * 
 * @param entityName - The entity name (optional)
 * @param iconClass - The icon class from EspoCRM (optional)
 * @returns Material-UI icon component, or TableChartIcon as default
 */
export function getNavigationIcon(
  entityName?: string | null,
  iconClass?: string | null
): ComponentType<any> {
  // First try entity name
  if (entityName) {
    const entityIcon = ENTITY_ICON_MAP[entityName];
    if (entityIcon) {
      return entityIcon;
    }
  }
  
  // Then try icon class
  if (iconClass) {
    const classIcon = ICON_CLASS_MAP[iconClass];
    if (classIcon) {
      return classIcon;
    }
  }
  
  // Default fallback
  return TableChartIcon;
}

/**
 * Register a custom entity icon mapping
 * Allows tenants to extend the icon mapping with custom entities
 * 
 * @param entityName - The entity name
 * @param icon - The Material-UI icon component
 */
export function registerEntityIcon(
  entityName: string,
  icon: ComponentType<any>
): void {
  ENTITY_ICON_MAP[entityName] = icon;
}

/**
 * Register a custom icon class mapping
 * Allows tenants to extend the icon class mapping
 * 
 * @param iconClass - The icon class
 * @param icon - The Material-UI icon component
 */
export function registerIconClass(
  iconClass: string,
  icon: ComponentType<any>
): void {
  ICON_CLASS_MAP[iconClass] = icon;
}

